import React, { useCallback, useContext, useEffect, useRef, useState } from 'react'
import { AuthContext } from '../../../../contexts/user-context/user-context'
import { useSnackBar } from '../../../../contexts/snackbar'
import { API_ENDPOINT } from '../../../../configurations/global.config'
import axios from 'axios'
import { Alert, Box, Button, Chip, CircularProgress, DialogActions, DialogContent, DialogTitle, FormControl, IconButton, InputAdornment, Paper, styled, Switch, TablePagination, TextField, Tooltip, Typography } from '@mui/material'
import GenericTable from '../../../common/generic-table/generic-table'
import { useImmer } from 'use-immer'
import { AccessTime, AttachMoney, AutoMode, Cached, CheckCircle, Checklist, Clear, HourglassBottom, ImportExportRounded, LocalShippingOutlined, LooksOne, OpenInNew, PersonOutline, Refresh, SkipNext, SyncLock, ThumbDown, ThumbUp } from '@mui/icons-material'
import { displayMoney, Money } from '../../../../utils/money'
import Decimal from 'decimal.js'
import { format } from 'date-fns'
import CustomDialog from '../../../common/custom-dialog/custom-dialog'
import Grid2 from '@mui/material/Unstable_Grid2'
import { GiCrackedGlass } from 'react-icons/gi'
import { IconWithText } from '../../../common/styles/components'
import { OrgSettingsContext } from '../../../../contexts/org-settings-context'
import {
  IWoocommerceOrderImportTask,
  IWoocommerceSyncTask,
  TaskState,
} from '../../../../interfaces/common/async_tasks';
import FilterChips from '../../../common/filter-chips/filter-chips'
import { PaginationContext } from '../../../../contexts/pagination-context'
import { WoocommerceIntegrationConfig } from '../../../../interfaces/common/woocommerce';
import { WoocommercePackagingMode } from '../../../../enums/woocommerce';
import { IWoocommerceOrder } from '../../../../interfaces/delivery-company/woocommerce';
import AsyncTaskProgressBar from '../../../common/async-task-progress-bar/async-task-progress-bar';
import AsyncTaskList from '../../../common/async-task-list/async-task-list';
import WooCommerceIcon from '../../../common/woocommerce/icon'

interface ImportOrdersDialogProps {
    woocommerceOrders: IWoocommerceOrder[];
    onClose: () => void;
    integrationConfig: WoocommerceIntegrationConfig;
}

const ImportOrdersDialog = ({woocommerceOrders, onClose, integrationConfig}: ImportOrdersDialogProps) => {
    const [loading, setLoading] = useState(false);
    const [packagingMode, setPackagingMode] = useState(integrationConfig.packaging_mode);
    const [isFragile, setIsFragile] = useState(integrationConfig.default_is_fragile);
    const {showMessage} = useSnackBar()
    const {config} = useContext(AuthContext)
    const {color} = useContext(OrgSettingsContext)
    const [error, setError] = useState<string>()

    const handleImportOrders = async () => { 
        try {
            setLoading(true);
            await axios.post<{successes: number[], errors: number[]}>(`${API_ENDPOINT}/woocommerce/v1/orders/import/`, {
                woocommerce_order_numbers: Array.from(woocommerceOrders).map(order => order.order_number),
            }, config)
            onClose()
        } catch {
            showMessage('Something went wrong. Try again later or contact support.', 'error')
            setError('Something went wrong. Try again later or contact support.')
        } finally {
            setLoading(false);
        }
    }

    return <CustomDialog 
            open={true}
            onClose={onClose}
            maxWidth="sm"
            fullWidth
        >
            <DialogTitle align="center">Importing {woocommerceOrders.length} Woocommerce Orders</DialogTitle>
            <DialogContent>
                {
                    loading ? <Box display={"flex"} gap={2} justifyContent={"center"} alignItems={"center"}>Please wait while we import the orders... <CircularProgress size={20} /></Box> : 
                        <>
                        {error ? <Alert severity="error">{error}</Alert> : <>
                            <Grid2 container spacing={2}>
                                <Grid2 xs={12} md={6} sx={{borderRight: "1px solid #e0e0e0"}}>
                                    <Typography fontWeight={600} color={"primary"}> Packaging Mode 
                                    </Typography>
                                    <Typography variant="body2" color={"text.secondary"}>
                                    {packagingMode === WoocommercePackagingMode.AUTO ? "Split each item in the order into separate packages." :"Import each order as a single package."}
                                    </Typography>
                                    <Box display="flex" gap={1} mt={1}>
                                        <Chip 
                                        onClick={() => setPackagingMode(WoocommercePackagingMode.AUTO)}
                                        color={packagingMode === WoocommercePackagingMode.AUTO ? "primary" : "default"}
                                        label={<span>Automatic</span>} 
                                        disabled={loading}
                                        icon={<AutoMode />} 
                                        />
                                        
                                        <Chip 
                                            onClick={() => setPackagingMode(WoocommercePackagingMode.ONE_PACKAGE)}
                                            color={packagingMode === WoocommercePackagingMode.ONE_PACKAGE ? "primary" : "default"}
                                            label={<span>One Package</span>} 
                                            disabled={loading}
                                            icon={<LooksOne />} 
                                        />
                            
                                    </Box>
                                    </Grid2>

                                <Grid2 xs={12} md={6} sx={{borderLeft: "1px solid #e0e0e0", paddingLeft: 3}}>

                                    <Typography fontWeight={600} color={"primary"}> Fragile 
                                    </Typography>
                                    <Typography variant="body2" color={"text.secondary"}>
                                    {isFragile ? "All packages will be marked as fragile." : "Packages will NOT be marked as fragile."}
                                    </Typography>
                                    <Box display="flex" gap={1} mt={1}>
                                        <Switch
                                            checked={isFragile}
                                            onChange={() => setIsFragile(!isFragile)}
                                            disabled={loading}
                                        />
                                        <IconWithText> Mark as Fragile <GiCrackedGlass size={24} color={color} /></IconWithText>
                                    </Box>
                                </Grid2>
                            </Grid2>

                            <DialogActions sx={{justifyContent: "center", padding: 2, mt: 2}}>
                                <Button disabled={loading} onClick={handleImportOrders} variant="contained" color="primary">Confirm {loading && <CircularProgress size={20} sx={{ml: 2}}/>}</Button>
                                <Button disabled={loading} onClick={onClose} variant="outlined" color="error">Cancel</Button>
                            </DialogActions>

                        </>
                        }
                    </>
                    
                    
                }
            </DialogContent>

            

        </CustomDialog>
    
}

interface WoocommerceSyncTaskCardProps {
    status: TaskState;
}

const WoocommerceSyncTaskCard = styled(Paper)<WoocommerceSyncTaskCardProps>(({ theme, status }) => ({
    padding: theme.spacing(2),
    width: '100%',
    background: theme.palette.background.paper,
    borderRight: '6px solid',
    borderColor: theme.palette[status === 'FAILED'
      ? 'error'
      : status === 'COMPLETED'
      ? 'success'
      : 'info'].main,
    position: 'relative',
    overflow: 'hidden',
    marginBottom: theme.spacing(2),
  }));


export enum WoocommerceOrderStatus {
  PENDING_IMPORT = 'Pending Import',
  IMPORTED = 'Imported',
  FAILED = 'Failed',
  ALL_STATUSES = 'All Statuses',
}

export enum WoocommerceOrderPeriod {
  ALL_TIME = 'All Time',
  LAST_24_HOURS = 'Last 24 Hours',
  LAST_7_DAYS = 'Last 7 Days',
  LAST_30_DAYS = 'Last 30 Days',
}


function WoocommerceOrders({integrationConfig}: {integrationConfig: WoocommerceIntegrationConfig}) {
  const {config} = useContext(AuthContext)
  const [woocommerceOrders, setWoocommerceOrders] = useState<IWoocommerceOrder[] | null>(null)
  const [count, setCount] = useState(0)
  const {showMessage} = useSnackBar()
  const [loading, setLoading] = useState(true)
  const [woocommerceOrderNumberSearch, setWoocommerceOrderNumberSearch] = useState('')
  const [woocommerceOrderNumberSearchInput, setWoocommerceOrderNumberSearchInput] = useState('')
  const [statusFilter, setStatusFilter] = useState<WoocommerceOrderStatus>(WoocommerceOrderStatus.ALL_STATUSES)
  const [periodFilter, setPeriodFilter] = useState<WoocommerceOrderPeriod>(WoocommerceOrderPeriod.ALL_TIME)
  const [autoRefresh, setAutoRefresh] = useState(false)

  const [selectedOrders, setSelectedOrders] = useImmer<Map<string, IWoocommerceOrder>>(new Map())
  
  const [importingOrders, setImportingOrders] = useState(false)
  const { paginationConfig, setPaginationConfig } =
    useContext(PaginationContext);
  const [tablePage, setTablePage] = useState(0)


  const pollingRef = useRef<NodeJS.Timeout | null>(null);
  const syncPollingRef = useRef<NodeJS.Timeout | null>(null);

  const [pendingTasks, setPendingTasks] = useState<IWoocommerceOrderImportTask[]>([])
  const [syncTasks, setSyncTasks] = useState<IWoocommerceSyncTask[]>([])
  const [syncing, setSyncing] = useState(false)

  const fetchTasks = useCallback(async () => {
    try {
      const response = await axios.get<IWoocommerceOrderImportTask[]>(
        `${API_ENDPOINT}/v1/jobs/woocommerce-order-import/`,
        config,
      );
      console.log(response.data)
      setPendingTasks(response.data);

      const stillPending = response.data.some(
        (task) => task.status !== TaskState.COMPLETED && task.status !== TaskState.FAILED
      );

      if (!stillPending && pollingRef.current) {
        clearInterval(pollingRef.current);
        pollingRef.current = null;
      } else if (stillPending && !pollingRef.current) {
        pollingRef.current = setInterval(fetchTasks, 3000);
      }
    } catch (error) {
      console.error('Failed to fetch tasks:', error);
      showMessage('Failed to fetch tasks', 'error');
    }
  }, [config, showMessage]);

  const fetchSyncTasks = useCallback(async () => {
    try {
      const response = await axios.get<IWoocommerceSyncTask[]>(
        `${API_ENDPOINT}/v1/jobs/woocommerce-sync/`,
        config,
      );
      console.log('Sync tasks:', response.data)
      setSyncTasks(response.data);

      const stillPending = response.data.some(
        (task) => task.status !== TaskState.COMPLETED && task.status !== TaskState.FAILED
      );

      if (!stillPending && syncPollingRef.current) {
        clearInterval(syncPollingRef.current);
        syncPollingRef.current = null;
      } else if (stillPending && !syncPollingRef.current) {
        syncPollingRef.current = setInterval(fetchSyncTasks, 3000);
      }
    } catch (error) {
      console.error('Failed to fetch sync tasks:', error);
      showMessage('Failed to fetch sync tasks', 'error');
    }
  }, [config, showMessage]);

  const handleSync = useCallback(async () => {
    try {
      setSyncing(true);
      const response = await axios.post(
        `${API_ENDPOINT}/woocommerce/v1/orders/sync/`,
        {},
        config
      );
      
      if (response.status == 200) {
        showMessage("Sync already in progress", "info")
      } else {
        showMessage('Sync started successfully');
        fetchSyncTasks();
      }
      
    } catch (error) {
      console.error('Failed to start sync:', error);
      showMessage('Failed to start sync', 'error');
    } finally {
      setSyncing(false);
    }
  }, [config, showMessage, fetchSyncTasks]);

  useEffect(() => {
    fetchTasks();
    fetchSyncTasks();
    return () => {
      if (pollingRef.current) {
        clearInterval(pollingRef.current);
      }
      if (syncPollingRef.current) {
        clearInterval(syncPollingRef.current);
      }
    };
  }, [fetchTasks, fetchSyncTasks]);

  useEffect(()=>{
    setTablePage(0)
  }, [paginationConfig.rowsPerPage, woocommerceOrderNumberSearch, statusFilter, periodFilter])

  const getWoocommerceOrders = async () => {
    setLoading(true)
    setWoocommerceOrders(null)
    setCount(0)
    try {
            let endpoint = `${API_ENDPOINT}/woocommerce/v1/orders/?page_size=${paginationConfig.rowsPerPage}&page=${tablePage + 1}`
            if (woocommerceOrderNumberSearch && woocommerceOrderNumberSearch.trim() !== '') {
                endpoint += `&order_number=${woocommerceOrderNumberSearch}`
            }
            if (statusFilter !== WoocommerceOrderStatus.ALL_STATUSES) {
                endpoint += `&status=${statusFilter}`
            }
            if (periodFilter !== WoocommerceOrderPeriod.ALL_TIME) {
                endpoint += `&since_hours=${periodFilter === WoocommerceOrderPeriod.LAST_24_HOURS ? 24 : periodFilter === WoocommerceOrderPeriod.LAST_7_DAYS ? 7*24 : periodFilter === WoocommerceOrderPeriod.LAST_30_DAYS ? 30*24 : 0}`
            }
            const response = await axios.get<{count: number, results: IWoocommerceOrder[]}>(endpoint, config)
            setWoocommerceOrders(response.data.results)
            setCount(response.data.count)
        } catch (error) {
            console.log(error)
            showMessage('Failed to get Woocommerce orders', 'error')
        } finally {
            setLoading(false)
        }
    }
    

    useEffect(() => {
        getWoocommerceOrders()
    }, [woocommerceOrderNumberSearch, statusFilter, periodFilter, paginationConfig.rowsPerPage, tablePage])

    const hasCompletedTasks = pendingTasks.some(
        task => task.status === TaskState.COMPLETED || task.status === TaskState.FAILED
      );

    const hasCompletedSyncTasks = syncTasks.some(
        task => task.status === TaskState.COMPLETED || task.status === TaskState.FAILED
      );

    useEffect(() => {
        if (autoRefresh) {

            const interval = setInterval(() => {
                getWoocommerceOrders()
            }, 5000)
            return () => clearInterval(interval)
        }
    }, [autoRefresh, getWoocommerceOrders])

    


  return (
    <>

            <Box width="100%">
                <IconWithText centered><WooCommerceIcon size={50} />
                    <Typography variant="h4" component="h4" sx={{textAlign: 'center', mt: 3, mb: 3}}> Orders</Typography>
                </IconWithText>
            </Box>

            <Box width="100%">
                <Box mb={2} width="100%" gap={1} display="flex" justifyContent="space-between" flexWrap="wrap">
                  <Button onClick={()=>setImportingOrders(true)} disabled={selectedOrders.size === 0} size="small" variant="contained" color="primary"><Checklist fontSize='small' sx={{mr: 1}}/> Import</Button>
                  <Box display="flex" gap={1}>
                    <Tooltip title="Sync orders from WooCommerce to the system database" arrow>
                      <span>
                        <Button onClick={handleSync} disabled={syncing} size="small" variant="contained" color="info"><Cached fontSize='small' sx={{mr: 1}}/> Sync Orders {syncing && <CircularProgress size={16} sx={{ml: 1}}/>}</Button>
                      </span>
                    </Tooltip>
                    <Tooltip title="Refresh the current page to show latest synced orders" arrow>
                      <Button onClick={getWoocommerceOrders} size="small" variant="contained" color="info"><Refresh fontSize='small' sx={{mr: 1}}/> Refresh</Button>
                    </Tooltip>
                  </Box>
                </Box>
            </Box>
                
                <AsyncTaskList
                  tasks={pendingTasks}
                  taskLabel="Importing Woocommerce Orders"
                  taskIcon={<ImportExportRounded />}
                  chipColor="primary"
                  processingText="Processing..."
                  borderPosition="left"
                  onTasksUpdated={setPendingTasks}
                  renderTaskStats={(task) => (
                    <Grid2 container spacing={1} width="100%">
                      <Grid2 xs={12} md={4}>
                        <IconWithText><ThumbUp fontSize='small' color='success' /> {task.result.success ?? 0} imported.</IconWithText>
                      </Grid2>
                      <Grid2 xs={12} md={4}>
                        <IconWithText><ThumbDown fontSize='small' color='error' /> {task.result.failed ?? 0} failed.</IconWithText>
                      </Grid2>
                      <Grid2 xs={12} md={4}>
                        <IconWithText><SkipNext fontSize='small' color='warning' /> {task.result.skipped ?? 0} already imported.</IconWithText>
                      </Grid2>
                    </Grid2>
                  )}
                />

                <AsyncTaskList
                  tasks={syncTasks.filter(t => t.status != TaskState.COMPLETED)}
                  taskLabel="Syncing Woocommerce Orders"
                  taskIcon={<Cached />}
                  chipColor="info"
                  progressColor="info"
                  processingText="Syncing..."
                  borderPosition="right"
                  dismissButtonColor="info"
                  onTasksUpdated={setSyncTasks}
                  compact={true}
                  renderTaskStats={(task) => (
                    <Box display="flex" gap={2} justifyContent="center" alignItems="center">
                      <Chip
                        icon={<CheckCircle fontSize="small" />}
                        label={`${task.result?.created ?? 0} created`}
                        size="small"
                        color="success"
                        variant="outlined"
                      />
                      <Chip
                        icon={<Cached fontSize="small" />}
                        label={`${task.result?.updated ?? 0} updated`}
                        size="small"
                        color="info"
                        variant="outlined"
                      />
                    </Box>
                  )}
                />

                <Grid2 width="100%" container spacing={1} sx={{alignItems: 'center'}} >
                    <Grid2 xs={12} md={4}>
                        <FormControl fullWidth>
                            <TextField
                                label={'Woocommerce Order #'}
                                value={woocommerceOrderNumberSearchInput}
                                onChange={(e) => setWoocommerceOrderNumberSearchInput(e.target.value)}
                                variant="outlined"
                                sx={{maxWidth: 400, width: '100%'}}
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter') {
                                        setWoocommerceOrderNumberSearch(woocommerceOrderNumberSearchInput)
                                        // should unfocus the input
                                    }
                                }}
                                InputProps={{
                                    endAdornment: (
                                        <InputAdornment position="end">
                                            {
                                              woocommerceOrderNumberSearchInput && <> {
                                               woocommerceOrderNumberSearchInput != woocommerceOrderNumberSearch ?
                                                        <Chip label={"Hit enter to search"} size="small" color="secondary" />
                                                        :
                                                        <IconButton onClick={() => {
                                                            setWoocommerceOrderNumberSearch('')
                                                            setWoocommerceOrderNumberSearchInput('')
                                                        }}>
                                                            <Clear />
                                                        </IconButton>
                                                }</>
                                                
                                            }
                                        </InputAdornment>
                                    )
                                }}
                            />
                        </FormControl>
                    </Grid2>

                    <Grid2 xs={12} md={4}>

                    <FilterChips 
                        label={'Status'}
                        options={[WoocommerceOrderStatus.ALL_STATUSES, WoocommerceOrderStatus.PENDING_IMPORT, WoocommerceOrderStatus.IMPORTED, WoocommerceOrderStatus.FAILED]}
                        selected={[statusFilter]} 
                        onChange={(selected) => setStatusFilter(selected[0] as WoocommerceOrderStatus)}
                    />
                    </Grid2>
                    <Grid2 xs={12} md={4}>

                    <FilterChips 
                        label={'Period'}
                        multiple={false}
                        options={[WoocommerceOrderPeriod.ALL_TIME, WoocommerceOrderPeriod.LAST_24_HOURS, WoocommerceOrderPeriod.LAST_7_DAYS, WoocommerceOrderPeriod.LAST_30_DAYS]}
                        selected={[periodFilter]} 
                        onChange={(selected) => setPeriodFilter(selected[0] as WoocommerceOrderPeriod)}
                    />
                    </Grid2>
                </Grid2>

                
        {loading ? <CircularProgress /> :
          woocommerceOrders == null ? <Alert severity="error">Oops, something went wrong. Please try refreshing the page of contact us if the issue persists.</Alert> :
         <>
            <Box width="100%" display="flex" justifyContent="space-between" alignItems="center">
                <Chip label={`Selected ${selectedOrders.size}/${count}`} />
                <TablePagination
                        component="div"
                        onPageChange={(event, newPage) => setTablePage(newPage)}
                        page={tablePage}
                        count={count}
                        rowsPerPage={paginationConfig.rowsPerPage}
                        onRowsPerPageChange={(event) => {
                            const newRowsPerPage = parseInt(event.target.value, 10);
                            setPaginationConfig({ ...paginationConfig, rowsPerPage: newRowsPerPage });
                            setTablePage(0);
                        }}
                        rowsPerPageOptions={[5, 10, 25, 50, 100, 200, 250, 500, 1000]}
                        labelRowsPerPage=""
                        style={{ overflow: 'visible' }}
                    />
            </Box>

            <Box display="flex" alignItems="center" justifyContent="flex-start" width="100%">
                <Switch checked={autoRefresh} onChange={(e) => setAutoRefresh(!autoRefresh)} /> Auto Refresh every 5 seconds
            </Box>
            
            
                
                <GenericTable<IWoocommerceOrder>
                        sourceView="woocommerce-orders"
                        items={woocommerceOrders}
                        isLoading={loading}
                        fullWidth
                        columns={[
                            { id: 'order_id', header: <IconWithText centered><WooCommerceIcon size={32} /> Order #</IconWithText>, display_body: (order) => <a href={order.woocommerce_order_url} target="_blank" rel="noopener noreferrer"><Chip  label={order.order_number} color={"secondary"} size="small" icon={<OpenInNew fontSize='small' />} sx={{cursor: 'pointer',}}/></a> },
                            { id: 'PT Order', header: <IconWithText centered><LocalShippingOutlined color={"primary"}  /> Shipping Order</IconWithText>, display_body: (order) => <>{order.order ? <a href={`/order/${order.order}`}><Chip  label={order.order} color="primary" size="small" icon={<OpenInNew fontSize='small' />} sx={{cursor: 'pointer',}}/></a> : <SyncLock color="primary" />}</> },
                            { id: 'customer', header: <IconWithText centered><PersonOutline color={"primary"}  /> Recipient</IconWithText>, display_body: (order) => {
                                console.log(order.recipient_info)
                                const {name, phone, address} = order.recipient_info
                                return <Box>
                                    {name}
                                    <br />
                                    {phone}
                                    <br />
                                    {address}
                                </Box>
                            }},
                            { id: 'Amount', header: <IconWithText centered><AttachMoney color={"primary"}  /> Amount</IconWithText>, display_body: (order) => <>{displayMoney([[order.currency , Decimal(order.total_price)]] as Money[])}</> },
                            { id: 'Last Sync', header: <IconWithText centered><AccessTime color={"primary"}  /> Last Sync</IconWithText>, display_body: (order) => <>{order.updated_at ? format(order.updated_at, 'dd-MM-yyyy HH:mm') : 'Never'}</> },
                            { id: 'Status', header: 'Status', display_body: (order) => {
                                if (order.validation_errors) return <Chip label={<Tooltip title={<Alert sx={{zIndex: 1000}} severity="error"><ul>{order.validation_errors.split(';').map((err, index) => <li key={index}>{err}</li>)}</ul></Alert>}><Typography variant="caption">{order.validation_errors.split(';').length} errors</Typography></Tooltip>} color="error" />
                                if (!order.order) return <IconWithText centered><HourglassBottom fontSize='small' color='info' /> Pending Import</IconWithText>
                                return <IconWithText centered><CheckCircle fontSize='small' color='success' /> Imported</IconWithText>
                            }},
                        ]} 
                        selectedItems={selectedOrders} 
                        updateSelectedItems={setSelectedOrders}
                        selectionEnabled
                        getItemUID={(item: IWoocommerceOrder) => item.order_number}
                />
            </>
        }

                {
                    importingOrders && <ImportOrdersDialog
                    woocommerceOrders={Array.from(selectedOrders.values())}
                        onClose={()=>{
                            setImportingOrders(false)
                            fetchTasks()
                            setSelectedOrders(new Map())
                        }
                        } 
                        integrationConfig={integrationConfig}
                    />
                }

    
    </>
    
  )
}

export default WoocommerceOrders
